/**
 * Who may see which Pulse tab, and what the top menu calls Pulse for them.
 *
 * The store, both nav components and the router guard all answer that question, and they
 * must answer it identically. The only way to guarantee that is one module they all call.
 * The store cannot serve the router directly: auth.module.js imports the router, so a
 * router-to-store import would close a cycle.
 *
 * Nothing here touches Vue, Vuex or vue-router. Everything takes the plain userData object
 * that TokenService.getUserData() returns.
 *
 * These roles only show or hide menu items. They are deliberately not mapped to HmRag
 * semantic roles on the server (owner decision, 2026-09-24).
 */

/**
 * Role Ids exactly as dbo.AspNetRoles spells them.
 *
 * The token's "roles" value is a comma-joined list of AspNetRoles.Id, built in
 * ApplicationOAuthProvider.CreateProperties. Matching is case sensitive.
 */
export const PULSE_ROLE_IDS = {
  wiki: 'pulsewiki',
  wikiAndChat: 'pulsewikichat'
}

/** The three route names that live under the PULSE top menu. */
export const PULSE_ROUTES = {
  chat: 'item-search',
  wiki: 'item-wiki',
  search: 'item-product-search'
}

/**
 * Top menu label. A user who can only search sees the menu's pre-PULSE name, since for them
 * it is still just the item search page. Vuetify renders tab labels uppercase.
 */
export const PULSE_MENU_TITLES = {
  pulse: 'PULSE',
  search: 'Item / Product Search'
}

/**
 * Exact, case-sensitive membership test against the token's role list.
 *
 * Deliberately not String.includes. The rest of auth.module.js tests roles with
 * includes() against the joined string, which is a substring match: under that idiom
 * 'pulsewikichat'.includes('pulsewiki') is true. It happens to give the right answer for
 * these two roles, but that is a coincidence of the names rather than a rule. Pulse access
 * expresses the hierarchy in code instead, so renaming a role cannot quietly change who gets in.
 */
export function hasRole (userData, roleId) {
  if (!userData || !userData.roles || !roleId) return false

  const roles = Array.isArray(userData.roles)
    ? userData.roles
    : String(userData.roles).split(',')

  return roles.some(role => String(role).trim() === roleId)
}

/** Wiki is granted by the wiki role or by its superset. */
export function canSeePulseWiki (userData) {
  return hasRole(userData, PULSE_ROLE_IDS.wiki) ||
    hasRole(userData, PULSE_ROLE_IDS.wikiAndChat)
}

/** Chat is granted only by the combined role. Never by wiki alone. */
export function canSeePulseChat (userData) {
  return hasRole(userData, PULSE_ROLE_IDS.wikiAndChat)
}

/**
 * Whether to render the Pulse tab bar at all.
 *
 * False means the user may use only Search. A tab bar holding a single tab reads as a
 * mistake, so the whole nav, brand row included, is left out for those users.
 */
export function canSeePulseTabs (userData) {
  return canSeePulseWiki(userData) || canSeePulseChat(userData)
}

/**
 * Where the PULSE top menu should send this user. Returns a route name, not a path.
 *
 * The default is search, not chat: 'item-search' is the Chat page, so sending a user with no
 * Pulse role there would land them somewhere the router guard will not let them stay.
 */
export function pulseLandingRoute (userData) {
  if (canSeePulseChat(userData)) return PULSE_ROUTES.chat
  if (canSeePulseWiki(userData)) return PULSE_ROUTES.wiki
  return PULSE_ROUTES.search
}

/** The top menu label for this user. */
export function pulseMenuTitle (userData) {
  return canSeePulseTabs(userData) ? PULSE_MENU_TITLES.pulse : PULSE_MENU_TITLES.search
}

/**
 * Route-guard form of the checks above.
 *
 * `pulseTab` comes from the route's meta.pulseTab. Any route without it is not a gated
 * Pulse route and passes straight through, which keeps the guard a no-op for the rest of
 * the application.
 */
export function canEnterPulseTab (userData, pulseTab) {
  if (pulseTab === 'chat') return canSeePulseChat(userData)
  if (pulseTab === 'wiki') return canSeePulseWiki(userData)
  return true
}
